import { useState, useSyncExternalStore } from 'react';
import { ClipboardCheck,X, Save, FileText, Users, Trash2 } from '../../icons';
import { patchProject, type TaskProjectDto } from '../../api/tasks';
import type { TeamDto } from '../../api/project-teams';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { stripColor } from '../TaskPage/ProjectTopBar';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import { confirmAndDeleteProject } from './confirmDeleteProject';

interface ProjectSettingsModalProps {
  project: TaskProjectDto;
  /** 위임 대상 후보 = 내가 속한 팀. 서버도 "대상 팀의 멤버만 위임 가능"을 강제한다. */
  teams: TeamDto[];
  onClose: () => void;
  /** 저장 성공 — 갱신된 프로젝트. */
  onSaved: (project: TaskProjectDto) => void;
  /** 삭제 성공 — 호출부가 목록 갱신·선택 해제. */
  onDeleted: () => void;
  isLeaving?: boolean;
}

/** Radix SelectItem 은 빈 문자열 value 불가 — 무소속(개인) 센티널. ProjectCreateModal 과 동일. */
const TEAM_NONE = 'none';

/**
 * ProjectSettingsModal (2026-08-05) — 프로젝트 상세 헤더의 [설정] 진입점.
 *
 * 종전에는 프로젝트를 만든 뒤 이름·설명·링크를 고칠 UI 가 없었고 삭제도 좌측 목록 카드의
 * hover 휴지통뿐이었다. 생성자 본인에게 수정·삭제·위임을 한곳에 모은다.
 *
 * 폼 구조·클래스(`pj-modal*`)는 ProjectCreateModal 을 그대로 따른다 — 신규 파일이라
 * 기존 모달은 건드리지 않는다. 삭제는 기존 `confirmAndDeleteProject`(할일 개수·제목
 * 미리보기 컨펌 포함)를 그대로 재사용한다.
 *
 * 변경분만 PATCH 한다 — 서버 PATCH 는 `NO_FIELDS_TO_UPDATE` 를 400 으로 돌려주므로
 * 바뀐 게 없으면 요청 자체를 보내지 않는다.
 */
export function ProjectSettingsModal({
  project, teams, onClose, onSaved, onDeleted, isLeaving = false,
}: ProjectSettingsModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  const [name, setName] = useState(project.name ?? '');
  const [description, setDescription] = useState(project.description ?? '');
  const [linkEnabled, setLinkEnabled] = useState(!!project.linkUrl);
  const [linkUrl, setLinkUrl] = useState(project.linkUrl ?? '');
  const [requireApproval, setRequireApproval] = useState(project.requiresDoneApproval === true);
  const [teamId, setTeamId] = useState<string>(project.teamId ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeClose(onClose, submitting);
  const selectedTeamName = teams.find((t) => t.id === teamId)?.name;
  const originalTeamId = project.teamId ?? '';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError(tProject('project.create.err_name'));
      return;
    }
    const trimmedLink = linkEnabled ? linkUrl.trim() : '';
    if (linkEnabled && !/^https?:\/\//.test(trimmedLink)) {
      setError(tProject('project.create.link_err'));
      return;
    }

    // 변경분만 담는다 — 서버는 빈 body 를 400 NO_FIELDS_TO_UPDATE 로 거부한다.
    const patch: Parameters<typeof patchProject>[1] = {};
    if (name.trim() !== (project.name ?? '')) patch.name = name.trim();
    if (description.trim() !== (project.description ?? '')) patch.description = description.trim();
    const nextLink = trimmedLink || null;
    if (nextLink !== (project.linkUrl ?? null)) patch.linkUrl = nextLink;
    if (teamId !== originalTeamId) patch.teamId = teamId || null;
    // 완료 승인 필요 — 켜고 끄기(정본 이관 2026-10-12). 서버가 받는 길은 projects.routes.ts 의 ajv ＋ 저장 줄.
    if (requireApproval !== (project.requiresDoneApproval === true)) patch.requiresDoneApproval = requireApproval;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const updated = await patchProject(project.id, patch);
      onSaved(updated);
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      // 위임 대상 팀의 비-멤버는 서버가 팀 존재를 숨기려고 404 TEAM_NOT_FOUND 를 준다.
      if (code === 'TEAM_NOT_FOUND') {
        setError(tProject('project.settings.err_team_not_found'));
      } else {
        const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
        setError(`${code ? `[${code}] ` : ''}${msg ?? tProject('project.settings.err_save')}`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      const deleted = await confirmAndDeleteProject(project);
      if (deleted) onDeleted();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={`pj-modal-backdrop${isLeaving ? ' pj-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.settings.title')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`pj-modal pj-modal--detail pj-modal--strip${isLeaving ? ' pj-modal--leaving' : ''}`}>
        <span
          className="pj-modal-strip"
          style={{ background: teamId ? stripColor(teamId) : 'var(--primary)' }}
          aria-hidden="true"
        />
        <header className="pj-modal-header">
          <h2>{tProject('project.settings.title')}</h2>
          <button type="button" className="pj-modal-close" onClick={onClose} disabled={submitting} aria-label={tProject('project.btn.close')} title={tProject('project.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <form className="pj-modal-body" onSubmit={handleSubmit}>
          {/* ── 기본 정보 ── */}
          <section className="pj-form-section" aria-label={tProject('project.create.section_basic')}>
            <h3 className="pj-form-section-head">
              <span className="pj-team-section-icon" aria-hidden="true"><FileText size={13} /></span>
              {tProject('project.create.section_basic')}
            </h3>
            <div className="pj-modal-field">
              <div className="pj-field-head">
                <label htmlFor="pj-settings-name">
                  {tProject('project.create.label_name')}
                  <em className="pj-req" aria-hidden="true">*</em>
                </label>
                <span className="pj-field-counter" aria-label={tProject('project.create.name_counter_aria')}>
                  {name.length}/100
                </span>
              </div>
              <input
                id="pj-settings-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={tProject('project.create.name_placeholder')}
                maxLength={100}
                required
                aria-required="true"
                autoFocus
                disabled={submitting}
              />
              <label className={`pj-link-toggle${!name.trim() ? ' pj-link-toggle--disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={linkEnabled}
                  disabled={submitting || !name.trim()}
                  onChange={(e) => { setLinkEnabled(e.target.checked); if (!e.target.checked) setLinkUrl(''); }}
                />
                {tProject('project.create.link_toggle')}
              </label>
              {linkEnabled && (
                <div className="pj-link-url-row">
                  <label htmlFor="pj-settings-link" className="pj-link-url-label">{tProject('project.create.link_label')}</label>
                  <input
                    id="pj-settings-link"
                    type="url"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder={tProject('project.create.link_placeholder')}
                    maxLength={500}
                    disabled={submitting}
                  />
                </div>
              )}
            </div>
            <div className="pj-modal-field">
              <label htmlFor="pj-settings-desc">{tProject('project.create.label_desc')}</label>
              <textarea
                id="pj-settings-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={tProject('project.create.desc_placeholder')}
                rows={3}
                maxLength={5000}
                disabled={submitting}
              />
            </div>
          </section>

          {/*
            ── 완료 승인 (2026-09-04) ──
            기본 정보 아래, 위임 위에 둔다 — '이 프로젝트가 어떻게 굴러가는가' 를 정하는
            설정이라 사람(위임)보다는 규칙(기본 정보) 쪽에 가깝다.
            체크박스 모양은 바로 위 링크 토글(.pj-link-toggle)을 그대로 재사용한다 —
            이 모달에 스위치 컴포넌트를 새로 들이지 않으려는 것.
            ⭐ 정본 이관 판 6 (2026-10-13) — 종전(10-12)에는 이 토글을 이름 칸 «안»에 넣어 두었다.
              정본 자리(기본 정보 아래 별도 section)로 옮겼다. 동작·서버 짝은 그대로다:
            ⛔ 이 토글이 돌려면 서버 네 자리가 함께 있어야 한다 —
              projects.routes.ts 의 ajv ＋ 저장 줄 ＋ select 넷 ＋ 응답 펴기.
              ajv 가 스키마 밖 칸을 «조용히 지우고 200 을 주므로»(400 이 아니다)
              한 자리만 빠져도 「눌러도 안 바뀐다」가 된다 ⇒ 왕복 가드가 그 짝을 잠근다.
          */}
          <section className="pj-form-section" aria-label={tProject('project.settings.require_approval')}>
            <h3 className="pj-form-section-head">
              <span className="pj-team-section-icon pj-team-section-icon--green" aria-hidden="true"><ClipboardCheck size={13} /></span>
              {tProject('project.settings.require_approval')}
            </h3>
            <div className="pj-modal-field">
              <label className="pj-link-toggle">
                <input
                  type="checkbox"
                  checked={requireApproval}
                  disabled={submitting}
                  onChange={(e) => setRequireApproval(e.target.checked)}
                />
                {tProject('project.settings.require_approval')}
              </label>
              <p className="pj-field-hint">{tProject('project.settings.require_approval_hint')}</p>
            </div>
          </section>

          {/* ── 위임 ── */}
          <section className="pj-form-section" aria-label={tProject('project.settings.label_delegate')}>
            <h3 className="pj-form-section-head">
              <span className="pj-team-section-icon pj-team-section-icon--green" aria-hidden="true"><Users size={13} /></span>
              {tProject('project.settings.label_delegate')}
            </h3>
            <div className="pj-modal-field">
              <label id="pj-settings-team-label" htmlFor="pj-settings-team">{tProject('project.create.label_team')}</label>
              <Select
                value={teamId || TEAM_NONE}
                onValueChange={(v) => setTeamId(v === TEAM_NONE ? '' : v)}
                disabled={submitting}
              >
                <SelectTrigger
                  id="pj-settings-team"
                  className="w-full"
                  aria-labelledby="pj-settings-team-label"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TEAM_NONE}>{tProject('project.create.team_none')}</SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {teamId !== originalTeamId && teamId && (
                <p className="pj-invite-note">
                  {selectedTeamName ? `${selectedTeamName} — ` : ''}{tProject('project.settings.delegate_note')}
                </p>
              )}
            </div>
          </section>

          {error && <div className="pj-modal-error" role="alert">{error}</div>}

          <footer className="pj-modal-footer pj-wiz-footer">
            <button
              type="button"
              className="pj-wiz-btn-ghost pj-settings-del"
              onClick={() => void handleDelete()}
              disabled={submitting}
            >
              <Trash2 size={13} />
              {tProject('project.settings.btn_delete')}
            </button>
            <span className="pj-settings-foot-spacer" />
            <button type="button" className="pj-wiz-btn-ghost" onClick={onClose} disabled={submitting}>
              {tProject('project.btn.cancel')}
            </button>
            <button type="submit" disabled={submitting || !name.trim()} className="pj-modal-submit pj-wiz-btn-primary">
              <Save size={13} />
              {submitting ? tProject('project.btn.saving') : tProject('project.settings.btn_save')}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
